/**
 * "My Points" card on the tech home screen (TECH-POINTS-PAGE.md §3).
 *
 * One line: current pay-period total + pending count, tapping opens the full
 * My Points page. The summary uses the payroll carry rule, so the total is
 * what this period's check counts, late work from earlier periods included;
 * the page adds the same late points to its day-by-day total. Hidden for salaried users — points don't
 * apply to them.
 */
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Star } from "lucide-react";
import { api } from "@/lib/api";
import { currentPayPeriod } from "@/lib/pay-period";
import { Card, CardContent } from "@/components/ui/card";

interface MeProfileResponse {
    profile: { pay: { payType: string; payPeriodDays: number } };
}

interface PointSummary {
    provisionalPoints: number | string;
    approvedPoints: number | string;
    /** Part of approvedPoints: earlier work that pays in this period's check. */
    latePoints?: number | string;
}

export function FieldPointsCard() {
    const navigate = useNavigate();

    const profileQ = useQuery({
        queryKey: ["me-profile"],
        queryFn: () => api.get<MeProfileResponse>("/api/me/profile"),
    });
    const pay = profileQ.data?.profile.pay;
    const period = currentPayPeriod(pay?.payPeriodDays ?? 14);

    const summaryQ = useQuery({
        queryKey: ["my-point-summary", period.start, period.end],
        queryFn: () =>
            api.get<PointSummary>(`/api/me/point-summary?periodStart=${period.start}&periodEnd=${period.end}`),
        enabled: pay != null && pay.payType !== "salary",
    });

    if (!pay || pay.payType === "salary") return null;

    const approved = Number(summaryQ.data?.approvedPoints ?? 0);
    const pending = Number(summaryQ.data?.provisionalPoints ?? 0);
    const late = Number(summaryQ.data?.latePoints ?? 0);
    const total = approved + pending;

    return (
        <Card
            className="cursor-pointer transition-colors hover:bg-secondary/30"
            onClick={() => navigate("/field/points")}
        >
            <CardContent className="flex min-h-[44px] items-center gap-3 p-4">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-yellow-500/15">
                    <Star className="h-4 w-4 fill-yellow-500 text-yellow-500" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">My Points</div>
                    <div className="text-xs text-foreground/70">
                        <span className="font-mono font-semibold tabular-nums">{total} pts</span>
                        {pending > 0 && <span> · {pending} pending</span>}
                        {late > 0 && <span> · {late} late from before</span>}
                        <span className="text-muted-foreground"> · this period</span>
                    </div>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-foreground/50" />
            </CardContent>
        </Card>
    );
}
